import { Tv, Home, Headphones, Wifi, Monitor, Shield, Anchor, ArrowUpRight } from "lucide-react";
import { Link } from "react-router-dom";
import { optimizeImage, buildSrcSet } from "@/utils/optimizeImage";

const SERVICES = [
  {
    id: "tv-mounting",
    icon: Tv,
    title: "Professional TV Mounting",
    desc: "Flush mounts, art-frame TVs, and motorized lifts with zero-visibility cable concealment. We coordinate with your interior designer and builder.",
    img: "https://images.unsplash.com/photo-1765766600807-1aa22f3dee72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwyfHx3YWxsJTIwbW91bnRlZCUyMHR2fGVufDB8fHx8MTc3NjQ2NDAxNHww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-7 md:row-span-2",
  },
  {
    id: "smart-home",
    icon: Home,
    title: "Smart Home Automation",
    desc: "UniFi, Sonos, Honeywell & Ring integration. Lighting scenes, smart thermostats, security cameras, and audio — all working seamlessly together.",
    img: "https://images.unsplash.com/photo-1774876549246-dfa8eae5d9f5?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzV8MHwxfHNlYXJjaHwyfHxzbWFydCUyMGhvbWUlMjBjb250cm9sJTIwcGFuZWx8ZW58MHx8fHwxNzc2NDY0MDEzfDA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5 md:row-span-2",
  },
  {
    id: "it-support",
    icon: Monitor,
    title: "Remote & On-Site IT Support",
    desc: "Fast troubleshooting, virus removal, device setup — remote or in-person for homes and businesses.",
    img: "https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzZXJ2ZXIlMjByb29tfGVufDB8fHx8MTc3NjQ2NDAwMXww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-4",
  },
  {
    id: "wifi-network",
    icon: Wifi,
    title: "Enterprise Wi-Fi & Networks",
    desc: "UniFi enterprise-grade networking with full property coverage, VLAN segmentation, and cybersecurity for large estates, businesses, and guest houses.",
    img: "https://images.unsplash.com/photo-1697618459160-aaabb2e07b6e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzZ8MHwxfHNlYXJjaHwxfHxmaWJlciUyMG9wdGljJTIwY2FibGVzJTIwZGFya3xlbnwwfHx8fDE3NzY0NjQwMTR8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-4",
  },
  {
    id: "home-theater",
    icon: Headphones,
    title: "Home Theater Design",
    desc: "Dolby Atmos surround sound, 4K laser projection, acoustic treatment, and dedicated cinema rooms for the ultimate private screening experience.",
    img: "https://images.unsplash.com/photo-1774301266018-57c0b190ed43?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBob21lJTIwdGhlYXRlcnxlbnwwfHx8fDE3NzY0NjQwMDF8MA&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-4",
  },
  {
    id: "business-it",
    icon: Shield,
    title: "Small Business IT Solutions",
    desc: "Digital signage, cybersecurity, server management, and complete IT infrastructure for your business.",
    img: "https://images.unsplash.com/photo-1555209183-8facf96a4349?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGxhcHRvcCUyMGRlc2slMjBkYXJrfGVufDB8fHx8MTc3NjQ2NDAxNHww&ixlib=rb-4.1.0&q=85",
    span: "md:col-span-5",
  },
  {
    id: "yacht-marine",
    icon: Anchor,
    title: "Yacht & Marine Tech",
    desc: "Smart entertainment, weatherproof displays, satellite Wi-Fi, navigation upgrades, and full automation for yachts and marine vessels across SWFL.",
    img: "https://images.pexels.com/photos/8356431/pexels-photo-8356431.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    span: "md:col-span-7",
  },
];

function ServiceCard({ service }) {
  const Icon = service.icon;
  return (
    <Link
      to={`/services/${service.id}`}
      data-testid={`service-card-${service.id}`}
      aria-label={`Learn more about ${service.title}`}
      className={`bento-item glass-card glass-card-hover relative group cursor-pointer block focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded-2xl ${service.span}`}
    >
      <img
        src={optimizeImage(service.img, { width: 800 })}
        srcSet={buildSrcSet(service.img, [400, 640, 800])}
        sizes="(min-width: 768px) 33vw, 100vw"
        alt={`${service.title} — professional installation in Southwest Florida`}
        loading="lazy"
        decoding="async"
        width="800"
        height="600"
        className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-45 transition-opacity duration-500"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#050A11] via-[#050A11]/60 to-transparent z-[1]" />

      {/* Top-right arrow affordance */}
      <div className="absolute top-4 right-4 z-[3] w-9 h-9 rounded-full bg-white/5 border border-white/10 flex items-center justify-center backdrop-blur-sm transition-all duration-300 group-hover:bg-blue-500/90 group-hover:border-blue-400 group-hover:scale-110">
        <ArrowUpRight
          size={16}
          className="text-slate-300 transition-transform duration-300 group-hover:text-white group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
        />
      </div>

      <div className="relative z-[2] h-full flex flex-col justify-end p-5 md:p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-lg bg-blue-600/20 flex items-center justify-center flex-shrink-0">
            <Icon size={18} className="text-blue-400" />
          </div>
          <h3 className="text-lg md:text-xl font-medium" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {service.title}
          </h3>
        </div>
        <p className="text-sm text-slate-400 leading-relaxed max-w-lg">
          {service.desc}
        </p>
        <span
          data-testid={`service-learn-more-${service.id}`}
          className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold tracking-wide text-blue-400 group-hover:text-blue-300 transition-colors"
        >
          Learn More
          <ArrowUpRight size={13} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </span>
      </div>
    </Link>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32" aria-label="Our Services">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <p className="text-xs font-bold tracking-[0.2em] uppercase text-blue-400 mb-4">
          What We Do
        </p>
        <h2
          className="text-3xl md:text-4xl tracking-tight font-medium mb-4"
          style={{ fontFamily: 'Outfit, sans-serif' }}
        >
          Expert Services, Tailored to You
        </h2>
        <p className="text-slate-400 text-lg max-w-2xl mb-12">
          From invisible TV installations in Port Royal estates to outfitting your yacht at Naples City Dock, we deliver premium technology solutions for Southwest Florida's most discerning clients.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 auto-rows-[220px] md:auto-rows-[240px]">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} service={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
